import React, { CSSProperties, FC, useState, useRef } from 'react'
import { useOutsideClick } from '@dashboard/hooks/useOutsideClick'
import classNamesBind from 'classnames/bind'
import css from './styles.module.scss'
import { DiscType, Profile } from '@dashboard/lib/types'
import { fullName } from '@dashboard/lib/profile'
import ProfileBubble from '@dashboard/components/ProfileBubble'
import { toArchetype } from '@dashboard/lib/personality'
import ColorIcon from '@dashboard/components/ColorIcon'
import { GroupsResponse } from '@dashboard/store/actions/groups'

interface OwnProps {
  group?: GroupsResponse
  profiles: Profile[]
  size: number
  discType: DiscType
  hideTooltip?: boolean

  hidePercentage?: boolean
}

const classNames = classNamesBind.bind(css)

const GroupProfileCluster: FC<OwnProps> = ({
  group,
  profiles,
  size,
  discType,
  hideTooltip,
  hidePercentage,
}: OwnProps) => {
  const [groupSelected, setGroupSelected] = useState(false)

  const ref = useRef<HTMLDivElement>(null)

  useOutsideClick(ref, () => {
    setGroupSelected(false)
  })

  const style: CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
  }

  const isLeader = (profile: Profile) => {
    return group && profile.id === group.leader.id
  }

  const handleGroupBubbleClick = () => {
    setGroupSelected(!groupSelected)
  }

  // Partials
  const renderProfileBubbleTooltip = () => {
    if (!groupSelected) return null
    return (
      <div className={classNames('tooltip')}>
        <div className={classNames('miniTableContainer', { hidePercentage })}>
          <div className={classNames('miniTable')}>
            {profiles.map(p => (
              <div key={p.id} className={classNames('profileLinkContainer')}>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={`/p/${p.id}`}
                  className={classNames('profile-link')}
                >
                  <div className={classNames('relative')} key={p.id}>
                    <div key={p.id} className={classNames('groupBubble')}>
                      <div className={classNames('relative')}>
                        <ProfileBubble profile={p} size={size} />
                        {isLeader(p) ? (
                          <div className={classNames('leaderStar')}>
                            <ColorIcon icon="star" discType="I" />
                          </div>
                        ) : null}
                      </div>
                      <div className={classNames('fullName')}>
                        {fullName(p)}
                      </div>
                      {p.personality?.disc_type ? (
                        <div
                          className={classNames(
                            'hover-title',
                            p.personality.disc_type,
                          )}
                        >
                          {toArchetype(p.personality?.disc_type)}(
                          {p.personality?.disc_type})
                        </div>
                      ) : null}
                    </div>
                    {isLeader(p) ? (
                      <span className={classNames('tipInfo', 'teamLeader')}>
                        Team leader
                      </span>
                    ) : null}
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderProfileImage = () => {
    return (
      <div
        onClick={handleGroupBubbleClick}
        ref={ref}
        className={classNames('profileImageContainer', discType)}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <ColorIcon icon="profiles-group" discType="white" />
      </div>
    )
  }

  const renderContent = () => {
    if (hideTooltip) return renderProfileImage()
    return (
      <div>
        {renderProfileImage()}
        {renderProfileBubbleTooltip()}
      </div>
    )
  }

  return (
    <div className={classNames('container')} style={style}>
      {renderContent()}
    </div>
  )
}

export default GroupProfileCluster
